import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import type { ServerACLL3Rule, ServerACLL4Rule } from '@/api/type-server'
import AddIcon from '@/assets/icons/add.svg?react'
import MenuIcon from '@/assets/icons/menu.svg?react'
import { ActionMenu } from '@/components/base/ActionMenu'
import { Loading } from '@/components/base/Loading'
import { Pagination } from '@/components/base/Pagination'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { Trans } from '@/components/Trans'
import {
  getEIPL4RuleCreatePath,
  getEIPL4RuleEditPath,
  useUnifiedContext,
} from '@/contexts/UnifiedContext'
import {
  ACL_L4_RULE_LIMIT,
  ACLAction,
  aclActionMeta,
  ACLDirection,
  aclL3RuleStateMeta,
  aclProtocolLabel,
  formatPortRange,
  isDefaultCIDR,
} from '@/models/acl'
import { formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

const L4RuleDeleteModal = lazy(() =>
  import('./L4RuleDeleteModal').then((m) => ({ default: m.L4RuleDeleteModal })),
)

const L4_PAGE_SIZE = 5

interface L3RuleRowProps {
  rule: ServerACLL3Rule
  onEdit?: (rule: ServerACLL3Rule) => void
  onDuplicate?: (rule: ServerACLL3Rule) => void
  onDelete?: (rule: ServerACLL3Rule) => void
}

export function L3RuleRow({ rule, onEdit, onDuplicate, onDelete }: L3RuleRowProps) {
  const { t } = useTranslation('eip')
  const ctx = useUnifiedContext()
  const actionMeta = aclActionMeta[rule.defaultAction as ACLAction] ?? aclActionMeta[ACLAction.Deny]
  const stateMeta = rule.state?.status != null ? aclL3RuleStateMeta[rule.state.status] : undefined
  const isDefault = isDefaultCIDR(rule.remoteCIDR)
  const { data: l4Total = 0 } = useQuery({
    queryKey: ['aclL4', ctx.mode, ctx.teamResID, ctx.memberResID, rule.aclResID, rule.id, 'count'],
    enabled: !isDefault,
    queryFn: async ({ signal }) => {
      const res = await API.getACLL4ExceptionRules(
        { acl_res_id: rule.aclResID, l3_rule_id: rule.id, page: 1, per_page: 1 },
        { signal },
      )
      if (res.code !== 0) throw new Error(res.msg)
      return res.pagination?.total ?? 0
    },
  })
  const isEditDisabledByL4 = !isDefault && l4Total > 0
  const isEditDisabled = isDefault || isEditDisabledByL4

  return (
    <CollapseTableRow
      colSpan={24}
      tableClassName="group !border-none ring-1 ring-neutral-200 rounded-xl"
      content={
        <div className="mx-4 rounded-lg group">
          <L4SubTable l3Rule={rule} />
        </div>
      }
    >
      <td colSpan={3} className="t-sm">
        <span className="inline-flex items-center gap-2">
          <span>{rule.remoteCIDR}</span>
          {isDefault && (
            <span className="t-xs px-1.5 py-0.5 rounded-full bg-[#0E121B52] text-white">
              <Trans ns="eip" i18nKey="aclRules.badgeDefault">
                Default
              </Trans>
            </span>
          )}
        </span>
      </td>
      <td colSpan={2} className="t-sm">
        {rule.direction === ACLDirection.Inbound ? (
          <Trans ns="eip" i18nKey="aclRules.directionInbound">
            Inbound
          </Trans>
        ) : (
          <Trans ns="eip" i18nKey="aclRules.directionOutbound">
            Outbound
          </Trans>
        )}
      </td>
      <td colSpan={3} className={cn('t-sm', actionMeta.cls)}>
        <Trans ns="eip" i18nKey={actionMeta.key}>
          {rule.defaultAction === ACLAction.Allow ? 'Allow' : 'Deny'}
        </Trans>
      </td>
      <td colSpan={2} className={cn('t-sm', stateMeta?.cls)}>
        {stateMeta ? (
          <Trans ns="eip" i18nKey={stateMeta.key}>
            {rule.state?.message || '-'}
          </Trans>
        ) : (
          '-'
        )}
      </td>
      <td colSpan={6} className="t-sm max-w-0">
        <Tooltip
          title={rule.description ? <span className="t-sm">{rule.description}</span> : null}
          mouseEnterDelay={0}
          arrow
        >
          <span className="overflow-hidden break-words [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
            {rule.description || '-'}
          </span>
        </Tooltip>
      </td>
      <td colSpan={5} className="t-sm">
        {rule.createdAt?.seconds ? formatDatePreset(rule.createdAt.seconds * 1000, 'second') : '-'}
      </td>
      <td colSpan={2} className="t-sm">
        <div className="flex items-center justify-end gap-2">
          <Tooltip
            title={
              isDefault ? (
                <span className="block text-left t-sm">
                  {t('aclRules.editL3DisabledByDefaultTooltip')}
                </span>
              ) : isEditDisabledByL4 ? (
                <span className="block text-left t-sm">
                  {t('aclRules.editL3DisabledByL4Tooltip')}
                </span>
              ) : null
            }
            mouseEnterDelay={0}
          >
            <span
              className={cn('t-sm', {
                'text-highlight cursor-pointer': !isEditDisabled,
                'text-tertiary cursor-not-allowed': isEditDisabled,
              })}
              aria-disabled={isEditDisabled}
              onClick={(e) => {
                e.stopPropagation()
                if (isEditDisabled) return
                onEdit?.(rule)
              }}
            >
              <Trans ns="eip" i18nKey="aclRules.actionEdit">
                Edit
              </Trans>
            </span>
          </Tooltip>
          <ActionMenu
            popupContainer="body"
            icon={<MenuIcon className="cursor-pointer w-4 h-4 text-tertiary" />}
            items={[
              {
                label: (
                  <Trans ns="eip" i18nKey="aclRules.actionDuplicate">
                    Duplicate
                  </Trans>
                ),
                onClick: () => onDuplicate?.(rule),
                disabled: isDefault,
              },
              {
                label: (
                  <Trans ns="eip" i18nKey="aclRules.actionDelete">
                    Delete
                  </Trans>
                ),
                onClick: () => onDelete?.(rule),
                type: 'danger',
                disabled: isDefault,
              },
            ]}
          />
        </div>
      </td>
    </CollapseTableRow>
  )
}

function L4SubTable({ l3Rule }: { l3Rule: ServerACLL3Rule }) {
  const { t } = useTranslation('eip')
  const aclResID = l3Rule.aclResID
  const l3RuleID = l3Rule.id
  const parentCIDR = l3Rule.remoteCIDR
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const { eipResId } = useParams<{ eipResId: string }>()
  const eipId = eipResId ?? ''
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [deleteRule, setDeleteRule] = useState<ServerACLL4Rule | undefined>()

  const goCreateL4 = () => {
    navigate(getEIPL4RuleCreatePath(ctx, eipId, l3RuleID), {
      state: { l3Rule },
    })
  }
  const goEditL4 = (l4: ServerACLL4Rule) => {
    navigate(getEIPL4RuleEditPath(ctx, eipId, l3RuleID, l4.id), {
      state: { l3Rule, rule: l4 },
    })
  }
  const goDuplicateL4 = (l4: ServerACLL4Rule) => {
    navigate(getEIPL4RuleCreatePath(ctx, eipId, l3RuleID), {
      state: { l3Rule, template: l4 },
    })
  }

  const { data, isLoading } = useQuery({
    queryKey: ['aclL4', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3RuleID, page],
    queryFn: async ({ signal }) => {
      const res = await API.getACLL4ExceptionRules(
        { acl_res_id: aclResID, l3_rule_id: l3RuleID, page, per_page: L4_PAGE_SIZE },
        { signal },
      )
      if (res.code !== 0) throw new Error(res.msg)
      return { rules: res.l4Rules ?? [], total: res.pagination?.total ?? 0 }
    },
  })

  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: ['aclL4', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3RuleID],
    })
  }

  const rules = data?.rules ?? []
  const total = data?.total ?? 0
  const isL4LimitReached = total >= ACL_L4_RULE_LIMIT

  return (
    <div className="bg-neutral-50 rounded-xl pt-2 px-4 pb-4">
      <table className="w-full table-fixed border-separate border-spacing-0">
        <colgroup>
          <col className="w-[140px]" />
          <col className="w-[140px]" />
          <col className="w-[140px]" />
          <col />
          <col className="w-[200px]" />
          <col className="w-[87px]" />
        </colgroup>
        <thead>
          <tr className="[&>th]:py-3 [&>th]:px-4 [&>th]:text-left [&>th]:t-x-sb [&>th]:text-tertiary">
            <th>
              <Trans ns="eip" i18nKey="aclRules.l4Policy">
                Policy
              </Trans>
            </th>
            <th>
              <Trans ns="eip" i18nKey="aclRules.l4Protocol">
                Protocol
              </Trans>
            </th>
            <th>
              <Trans ns="eip" i18nKey="aclRules.l4PortRange">
                Port Range
              </Trans>
            </th>
            <th>
              <Trans ns="eip" i18nKey="aclRules.colDescription">
                Description
              </Trans>
            </th>
            <th>
              <Trans ns="eip" i18nKey="aclRules.colCreatedTime">
                Created Time
              </Trans>
            </th>
            <th>
              <Trans ns="eip" i18nKey="aclRules.colAction">
                Action
              </Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} className="py-8">
                <div className="flex justify-center">
                  <Loading />
                </div>
              </td>
            </tr>
          ) : rules.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-6 text-center t-sm text-tertiary">
                <Trans ns="eip" i18nKey="aclRules.l4Empty">
                  No L4 exception rules.
                </Trans>
              </td>
            </tr>
          ) : (
            rules.map((r, index) => {
              const policyMeta =
                aclActionMeta[r.action as ACLAction] ?? aclActionMeta[ACLAction.Allow]
              return (
                <tr
                  key={r.id}
                  className={cn(
                    'h-11 bg-panel-white [&>td]:py-3 [&>td]:px-4 [&>td]:border-b [&>td]:border-neutral-100',
                    index === 0 &&
                      '[&>td:first-child]:rounded-tl-lg [&>td:last-child]:rounded-tr-lg',
                    index === rules.length - 1 &&
                      '[&>td]:border-b-0 [&>td:first-child]:rounded-bl-lg [&>td:last-child]:rounded-br-lg',
                  )}
                >
                  <td className={cn('t-xs whitespace-nowrap', policyMeta.cls)}>
                    <Trans ns="eip" i18nKey={policyMeta.key}>
                      {r.action === ACLAction.Allow ? 'Allow' : 'Deny'}
                    </Trans>
                  </td>
                  <td className="t-x-sb text-secondary whitespace-nowrap">
                    {aclProtocolLabel(r.protocol, r.portStart, r.portEnd)}
                  </td>
                  <td className="t-x-sb text-secondary whitespace-nowrap">
                    {formatPortRange(r.portStart, r.portEnd)}
                  </td>
                  <td className="t-x-sb text-secondary min-w-0">
                    <Tooltip title={r.description || null} mouseEnterDelay={0}>
                      <span className="block truncate">{r.description || '-'}</span>
                    </Tooltip>
                  </td>
                  <td className="t-x-sb text-tertiary whitespace-nowrap">
                    {r.createdAt?.seconds
                      ? formatDatePreset(r.createdAt.seconds * 1000, 'second')
                      : '-'}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-4">
                      <span
                        className="text-highlight cursor-pointer t-x-sb"
                        onClick={(e) => {
                          e.stopPropagation()
                          goEditL4(r)
                        }}
                      >
                        <Trans ns="eip" i18nKey="aclRules.actionEdit">
                          Edit
                        </Trans>
                      </span>
                      <ActionMenu
                        popupContainer="body"
                        icon={<MenuIcon className="cursor-pointer w-4 h-4 text-tertiary" />}
                        items={[
                          {
                            label: (
                              <Trans ns="eip" i18nKey="aclRules.actionDuplicate">
                                Duplicate
                              </Trans>
                            ),
                            onClick: () => goDuplicateL4(r),
                          },
                          {
                            label: (
                              <Trans ns="eip" i18nKey="aclRules.actionDelete">
                                Delete
                              </Trans>
                            ),
                            onClick: () => setDeleteRule(r),
                            type: 'danger',
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between mt-2">
        <Tooltip
          title={
            isL4LimitReached ? (
              <span className="t-sm">{t('aclRules.l4LimitReachedTooltip')}</span>
            ) : null
          }
          mouseEnterDelay={0}
        >
          <span
            className={cn(
              't-x-sb flex items-center gap-1 min-w-[88px] py-2 w-fit',
              isL4LimitReached
                ? 'text-tertiary cursor-not-allowed'
                : 'text-highlight cursor-pointer',
            )}
            aria-disabled={isL4LimitReached}
            onClick={(e) => {
              e.stopPropagation()
              if (isL4LimitReached) return
              goCreateL4()
            }}
          >
            <AddIcon className="w-4 h-4 shrink-0" />
            <Trans ns="eip" i18nKey="aclRules.newL4Rule">
              New L4 Rule
            </Trans>
          </span>
        </Tooltip>
        {total > 0 && (
          <Pagination
            size={L4_PAGE_SIZE}
            total={total}
            current={page}
            className="gap-2 [&>div]:w-8 [&>div]:h-8 [&>div]:rounded-lg [&>div]:t-x-sb"
            activeClassName="bg-transparent"
            navClassName="bg-transparent"
            onChange={setPage}
          />
        )}
      </div>

      <Suspense>
        {deleteRule && (
          <L4RuleDeleteModal
            aclResID={aclResID}
            rule={deleteRule}
            parentCIDR={parentCIDR}
            onSuccess={() => {
              setDeleteRule(undefined)
              refetch()
            }}
            onClose={() => setDeleteRule(undefined)}
          />
        )}
      </Suspense>
    </div>
  )
}
